/**
 * `useOrgTransactions` — live read of every transaction in the org
 * the caller has access to.
 *
 * Web port of mobile's `useProjectTotals.ts` (the data side, not the
 * aggregation): one snapshot listener filtered by orgId, sorted
 * newest-first by transaction date.
 *
 * Role-aware scope (mirrors `useProjectTransactions`):
 *   - SA / Admin / Manager / Accountant — server rule allows the
 *     unscoped org query; everything comes through.
 *   - Submit-only roles (Site Engineer / Supervisor) — server rule
 *     restricts reads to `createdBy == auth.uid`. The unscoped query
 *     would be rejected entirely by the rule's static validator,
 *     leaving the Dashboard empty with a `permission-denied` error.
 *     We append the per-creator filter explicitly so the listener
 *     stays compatible with the rule.
 *   - Viewer / Client — server rule scopes to `canSeeProject`
 *     membership; unscoped query is fine because the rule narrows
 *     the result set.
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { canSeeAnyTransactions, isSubmitOnlyTxnRole } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { db } from '@/lib/firebase';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import {
  txDateMs,
  type TransactionItem,
} from '@/lib/useProjectTransactions';

export type UseOrgTransactionsResult = {
  transactions: TransactionItem[];
  loading: boolean;
  error: string | null;
};

export function useOrgTransactions(
  orgId: string | null | undefined,
): UseOrgTransactionsResult {
  const { user } = useAuth();
  const { role: orgRole } = useCurrentOrgRole();
  const [items, setItems] = useState<TransactionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const scopeToOwn = isSubmitOnlyTxnRole(orgRole);
  const hasAnyTxnAccess = canSeeAnyTransactions(orgRole);
  const uid = user?.uid ?? null;

  useEffect(() => {
    if (!orgId) {
      setItems([]);
      setLoading(false);
      return;
    }
    // Roles with ZERO transaction access (Designer today) would fire
    // an unscoped org query the Firestore rule rejects in full. Skip
    // the listener entirely.
    if (!hasAnyTxnAccess) {
      setItems([]);
      setLoading(false);
      return;
    }
    // Submit-only roles need uid known before attaching listener —
    // without it the unscoped query gets rejected.
    if (scopeToOwn && !uid) {
      setItems([]);
      setLoading(true);
      return;
    }
    setLoading(true);
    setError(null);
    const constraints = [where('orgId', '==', orgId)];
    if (scopeToOwn && uid) {
      constraints.push(where('createdBy', '==', uid));
    }
    const unsub = onSnapshot(
      query(collection(db, 'transactions'), ...constraints),
      (snap) => {
        const rows: TransactionItem[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<TransactionItem, 'id'>),
        }));
        rows.sort((a, b) => txDateMs(b) - txDateMs(a));
        setItems(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useOrgTransactions] snapshot error:', err);
        setError(err.message);
        setLoading(false);
      },
    );
    return unsub;
  }, [orgId, scopeToOwn, hasAnyTxnAccess, uid]);

  return { transactions: items, loading, error };
}
